import { useMemo } from "react";
import { SEGMENT_APP_NAME } from "@granular/fabric3-definitions";
import { useOperatingContext } from "./operatingContext";

/**
 * Function that checks for the is_embedded query param. Mobile sets this
 * param when they load a web view.
 */
const getIsEmbedded = () => {
  const params = new URLSearchParams(globalThis.location.search);
  return params.has("isEmbedded");
};

/**
 * Get the current screen dimensions
 */
const getScreenDimensions = () => ({
  screenWidth: globalThis.screen?.width,
  screenHeight: globalThis.screen?.height,
});

type SegmentTraits = Record<string, unknown>;

/**
 * Hook that builds enriched Segment identity traits with context information.
 * Includes user's current context (agency, role), screen dimensions, and any additional custom properties.
 *
 * @param additionalTraits - Optional additional properties to include in the traits object
 * @returns Object containing the enriched traits
 *
 * @example
 * ```tsx
 * const traits = useSegmentIdentity({ customProp: 'value' });
 * // traits will include: appName, isEmbedded, agencyId, role, screenWidth, screenHeight, customProp
 * segment.identify(userId, traits);
 * ```
 *
 * @warning Do NOT pass personally identifiable information (PII) in additionalTraits.
 * This includes names, email addresses, phone numbers, or any other data that could
 * identify an individual. Segment data should contain only anonymized identifiers and
 * behavioral/usage data for privacy compliance.
 */
export function useSegmentIdentity(additionalTraits?: SegmentTraits) {
  const { contextSelected } = useOperatingContext();

  const traits = useMemo(() => {
    // Extract agency ID from contextId format "rootBusinessId~role~filteredBusinessId"
    const agencyId = contextSelected?.contextId?.split("~")[2];

    return {
      appName: SEGMENT_APP_NAME,
      isEmbedded: getIsEmbedded(),
      agencyId,
      role: contextSelected?.role,
      ...getScreenDimensions(),
      ...additionalTraits,
    };
  }, [contextSelected?.contextId, contextSelected?.role, additionalTraits]);

  return traits;
}
